import React, { useState } from 'react'
import { Btn, COLORS, Chip, DAY_NAMES, Drawer, INPUT_STYLE, Kpi, ProgressBar, fmtDate, fmtStamp, money, monthKey, pct, todayIso } from '../FieldOpsKit'
import { FREQUENCIES } from '../demoData'
import { goalOf, paceFraction, salesOf, storeState, visitStats } from '../store'

/**
 * Store detail (drawer): goal vs sales, agenda, orders, visit history and notes. Shared by the admin screens and
 * the salesperson's agenda. `official` = show only the published goal (what a salesperson is allowed to see).
 */
const VISIT_TONE = { done: 'green', missed: 'red', planned: 'blue', rescheduled: 'gray' }
const OUTCOME_LABEL = { order: 'Order taken', no_order: 'No order', closed: 'Store closed' }

export default function StoreDrawer({ ctx, storeId, official, onClose }) {
    const [text, setText] = useState('')
    const sales = ctx.state.sales
    const store = sales.stores.filter((s) => s.id === storeId)[0]
    if (!store) return null
    const month = monthKey(todayIso())
    const goal = goalOf(sales, month, store.id, official)
    const total = salesOf(sales, month, store.id)
    const st = storeState(goal, total, month)
    const visits = sales.visits.filter((v) => v.storeId === store.id).sort((a, b) => (a.date < b.date ? 1 : -1))
    const stats = visitStats(visits.filter((v) => monthKey(v.date) === month))
    const orders = sales.orders.filter((o) => o.storeId === store.id).sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, 6)
    const notes = (sales.notes[store.id] || []).slice().reverse()
    const freq = FREQUENCIES.filter((f) => f.key === store.freq)[0]
    const gap = goal === null ? 0 : Math.round(goal * paceFraction(month) - total)

    const addNote = () => {
        if (!text.trim()) return
        ctx.dispatch({ type: 'NOTE_ADD', storeId: store.id, text: text.trim(), by: ctx.me.name })
        setText('')
        ctx.toast('Note added.')
    }

    return (
        <Drawer show title={store.name} subtitle={store.route + ' · ' + store.city + ' · Salesperson ' + store.rep} onClose={onClose}
            footer={<Btn kind="secondary" onClick={onClose}>Close</Btn>}>
            <div className="row g-2 mb-3">
                <div className="col-6"><Kpi label="Goal" value={goal === null ? (official ? 'Not published' : 'No goal') : money(goal)} sub={st.label} status={st.tone === 'green' ? 'success' : st.tone === 'red' ? 'danger' : st.tone === 'amber' ? 'warning' : 'primary'} /></div>
                <div className="col-6"><Kpi label="Sales this month" value={money(total)} sub={goal ? pct(total, goal) + '% of the goal' : 'No goal to compare'} /></div>
            </div>
            {goal ? (
                <div style={{ marginBottom: 14 }}>
                    <ProgressBar value={total} max={goal} height={10} />
                    <div style={{ fontSize: '0.75rem', marginTop: 3, color: gap > 0 ? COLORS.amber : COLORS.green }}>{gap > 0 ? money(gap) + ' behind the pace for this point of the month' : 'On or ahead of pace'}</div>
                </div>
            ) : null}

            <h6 className="card-title-clean mb-2">Contact</h6>
            <div className="card mb-3"><div className="card-body" style={{ padding: '12px 16px', fontSize: '0.85rem' }}>
                <div>{store.address}, {store.city}</div>
                <div style={{ marginTop: 4 }}>
                    <a href={'tel:' + store.phone.replace(/[^0-9]/g, '')}>{store.phone}</a>
                    {' · '}
                    <a href={'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(store.address + ', ' + store.city)} target="_blank" rel="noopener noreferrer">Open in Maps</a>
                </div>
            </div></div>

            <h6 className="card-title-clean mb-2">Agenda</h6>
            <div className="card mb-3"><div className="card-body" style={{ padding: '12px 16px', fontSize: '0.85rem' }}>
                {DAY_NAMES[store.day]} · {freq ? freq.label : store.freq}
                <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <Chip tone="green">{stats.done} done</Chip><Chip tone="red">{stats.missed} missed</Chip><Chip tone="blue">{stats.upcoming} upcoming</Chip>
                </div>
            </div></div>

            <h6 className="card-title-clean mb-2">Recent orders</h6>
            <div className="card mb-3"><div className="card-body" style={{ padding: '4px 16px' }}>
                {orders.length === 0 ? <div style={{ padding: '10px 0', color: COLORS.muted, fontSize: '0.83rem' }}>No orders yet.</div> : orders.map((o) => (
                    <div key={o.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '8px 0', borderBottom: '1px solid ' + COLORS.soft, fontSize: '0.83rem' }}>
                        <span><strong>{o.soNo}</strong> {o.demo ? <Chip tone="purple">Demo</Chip> : null}<span style={{ color: COLORS.slate }}> · {fmtDate(o.date)}</span></span>
                        <strong>{money(o.total)}</strong>
                    </div>
                ))}
            </div></div>

            <h6 className="card-title-clean mb-2">Visit history</h6>
            <div className="card mb-3"><div className="card-body" style={{ padding: '4px 16px' }}>
                {visits.slice(0, 8).map((v) => (
                    <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '8px 0', borderBottom: '1px solid ' + COLORS.soft, fontSize: '0.83rem' }}>
                        <span>{fmtDate(v.date)}{v.extra ? <span style={{ color: COLORS.muted }}> · extra</span> : null}</span>
                        <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            {v.status === 'done' ? <span style={{ color: COLORS.slate }}>{OUTCOME_LABEL[v.outcome]}{v.reason ? ' — ' + v.reason : ''}</span> : null}
                            <Chip tone={VISIT_TONE[v.status]}>{v.status}</Chip>
                        </span>
                    </div>
                ))}
            </div></div>

            <h6 className="card-title-clean mb-2">Notes</h6>
            <div className="card mb-2"><div className="card-body" style={{ padding: '8px 16px' }}>
                {notes.length === 0 ? <div style={{ padding: '6px 0', color: COLORS.muted, fontSize: '0.83rem' }}>No notes yet.</div> : notes.map((n) => (
                    <div key={n.id} style={{ padding: '8px 0', borderBottom: '1px solid ' + COLORS.soft, fontSize: '0.83rem' }}>
                        {n.text}<div style={{ fontSize: '0.7rem', color: COLORS.muted }}>{n.by} · {fmtStamp(n.at)}</div>
                    </div>
                ))}
            </div></div>
            <div style={{ display: 'flex', gap: 8 }}>
                <input type="text" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addNote() }} placeholder="Add a note about this store" style={{ ...INPUT_STYLE, flex: 1 }} />
                <Btn onClick={addNote} disabled={!text.trim()}>Add</Btn>
            </div>
        </Drawer>
    )
}
